import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Banknote, CheckCircle, Calendar, Target, TrendingUp, AlertCircle, Briefcase, Loader2 } from 'lucide-react';
import { getJobDetailBySlug, getCvList, applyJob } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import SEO, { jobPostingSchema, breadcrumbSchema } from '../components/SEO';
import './JobDetailPage.css';

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatSalary(start, end) {
  if (!start && !end) return null;
  if (start && end) return `${start.toLocaleString('tr-TR')} - ${end.toLocaleString('tr-TR')} TL`;
  if (start) return `${start.toLocaleString('tr-TR')} TL`;
  return `${end.toLocaleString('tr-TR')} TL`;
}

// Kaynak veride satırlar genelde "- ", "• " veya "* " ile başlıyor; her satır
// zaten kendi CheckCircle ikonuyla listelendiği için bu işaretler burada
// temizlenmezse ikron yanında yinelenen bir "-" görünüyor.
function splitLines(text) {
  if (!text) return [];
  return text
    .split('\n')
    .map(l => l.trim().replace(/^[-•*]\s*/, ''))
    .filter(Boolean);
}

const JobDetailPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [loginOpen, setLoginOpen] = useState(false);

  const [cvList, setCvList] = useState([]);
  const [cvLoading, setCvLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getJobDetailBySlug(slug);
        setJob(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [slug]);

  // Başvuru için kayıtlı CV gerekiyor — girişliyse listeyi baştan çek
  useEffect(() => {
    if (!user?.candidateId) {
      setCvLoading(false);
      return;
    }
    getCvList(user.candidateId)
      .then((res) => setCvList((res.status && res.data) ? res.data : []))
      .catch(() => setCvList([]))
      .finally(() => setCvLoading(false));
  }, [user?.candidateId]);

  const handleApply = async () => {
    if (!user) {
      setLoginOpen(true);
      return;
    }
    if (!cvList.length || applying) return;
    setApplying(true);
    setApplyError(null);
    try {
      await applyJob({ jobId: job.id, cvId: cvList[0].id });
      setApplied(true);
    } catch (e) {
      setApplyError(e.message || 'Başvuru gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="job-detail__not-found">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-detail-page">
        <div className="job-detail__not-found">
          <h2>İlan Bulunamadı</h2>
          <Link to="/" className="btn btn--primary">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  const salary = formatSalary(job.startSalary, job.endSalary);
  const descriptionLines = splitLines(job.description);
  const requirementLines = splitLines(job.requirements);
  const responsibilityLines = splitLines(job.responsibilities);
  const qualificationLines = splitLines(job.preferredQualifications);
  const publishDate = formatDate(job.startAt);
  const endDate = formatDate(job.endAt);

  return (
    <div className="job-detail-page">
      {job && (
        <SEO
          title={`${job.title} - ${job.companyName || ''}`}
          description={`${job.title} iş ilanı${job.companyName ? ` - ${job.companyName}` : ''}${job.city ? ` - ${job.city}` : ''}. isBulsun.com'da detayları gör ve hemen başvur.`}
          path={`/ilan/${slug}`}
          type="article"
          structuredData={[jobPostingSchema(job), breadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: job.title }])]}
        />
      )}
      {/* Hero */}
      <div className="job-detail__hero">
        <div className="job-detail__hero-container">
          <Link to="/" className="job-detail__back">
            <ArrowLeft size={18} />
            <span>Geri Dön</span>
          </Link>

          <h1 className="job-detail__title">{job.title}</h1>

          <div className="job-detail__meta">
            {job.city && (
              <div className="job-detail__meta-item">
                <MapPin size={15} />
                <span>{job.city}</span>
              </div>
            )}
            {job.workingType && (
              <div className="job-detail__meta-item">
                <Briefcase size={15} />
                <span>{job.workingType}</span>
              </div>
            )}
            {salary && (
              <div className="job-detail__meta-item">
                <Banknote size={15} />
                <span>{salary}</span>
              </div>
            )}
            {publishDate && (
              <div className="job-detail__meta-item">
                <Calendar size={15} />
                <span>Yayınlanma: {publishDate}</span>
              </div>
            )}
            {endDate && (
              <div className="job-detail__meta-item job-detail__meta-item--end">
                <AlertCircle size={15} />
                <span>Son Başvuru: {endDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="job-detail__container">
        <div className="job-detail__layout">
          {/* Main */}
          <div className="job-detail__main">
            {job.matchScore != null && job.matchScore > 0 && (
              <div className="job-detail__match-section">
                <div className="job-detail__match-header">
                  <span>AI Uyum Puanı</span>
                  <span className="job-detail__match-value">%{job.matchScore}</span>
                </div>
                <div className="job-detail__match-bar">
                  <div className="job-detail__match-fill" style={{ width: `${job.matchScore}%` }}></div>
                </div>
              </div>
            )}

            {descriptionLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <Briefcase size={18} />
                  İş Tanımı
                </h2>
                <ul className="job-detail__list">
                  {descriptionLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {requirementLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <Target size={18} />
                  Aranan Nitelikler
                </h2>
                <ul className="job-detail__list">
                  {requirementLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {responsibilityLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <CheckCircle size={18} />
                  Sorumluluklar
                </h2>
                <ul className="job-detail__list">
                  {responsibilityLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {qualificationLines.length > 0 && (
              <div className="job-detail__section">
                <h2 className="job-detail__section-title">
                  <TrendingUp size={18} />
                  Tercih Edilen Nitelikler
                </h2>
                <ul className="job-detail__list">
                  {qualificationLines.map((line, i) => (
                    <li key={i} className="job-detail__list-item">
                      <CheckCircle size={16} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="job-detail__sidebar">
            <div className="job-detail__apply-card">
              {applied ? (
                <div className="job-detail__apply-success">
                  <CheckCircle size={20} />
                  <div>
                    <p className="job-detail__apply-success-title">Başvurunuz iletildi</p>
                    <p className="job-detail__apply-success-desc">
                      Başvurunuz{job.companyName ? ` ${job.companyName} firmasına` : ''} iletilmiştir.
                      Durumunu <Link to="/panel/basvurularim">Başvurularım</Link> sayfasından takip edebilirsiniz.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <h4 className="job-detail__apply-title">Bu İlana Başvur</h4>
                  <p className="job-detail__apply-desc">
                    {!user
                      ? 'Başvurmak için giriş yapmanız gerekiyor.'
                      : cvLoading
                        ? 'CV\'leriniz kontrol ediliyor...'
                        : cvList.length === 0
                          ? 'Başvurmak için önce bir CV oluşturmanız gerekiyor.'
                          : 'Kayıtlı CV\'nizle bu ilana başvurun.'}
                  </p>

                  {!user ? (
                    <button className="btn btn--primary btn--full" onClick={() => setLoginOpen(true)}>
                      Giriş Yap ve Başvur
                    </button>
                  ) : cvLoading ? (
                    <button className="btn btn--primary btn--full" disabled>
                      <Loader2 size={16} className="job-detail__apply-spinner" /> Kontrol ediliyor...
                    </button>
                  ) : cvList.length === 0 ? (
                    <Link to="/panel/cv-olustur" className="btn btn--primary btn--full">CV Oluştur</Link>
                  ) : (
                    <button className="btn btn--primary btn--full" onClick={handleApply} disabled={applying}>
                      {applying ? (
                        <><Loader2 size={16} className="job-detail__apply-spinner" /> Gönderiliyor...</>
                      ) : 'Başvur'}
                    </button>
                  )}

                  {applyError && (
                    <p className="job-detail__apply-error"><AlertCircle size={13} /> {applyError}</p>
                  )}
                </>
              )}
            </div>

            <div className="job-detail__info-card">
              <h4 className="job-detail__info-title">İlan Bilgileri</h4>
              {job.city && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Konum</span>
                  <span className="job-detail__info-value">{job.city}</span>
                </div>
              )}
              {job.workingType && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Çalışma Şekli</span>
                  <span className="job-detail__info-value">{job.workingType}</span>
                </div>
              )}
              {salary && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Maaş</span>
                  <span className="job-detail__info-value">{salary}</span>
                </div>
              )}
              {publishDate && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Yayınlanma</span>
                  <span className="job-detail__info-value">{publishDate}</span>
                </div>
              )}
              {endDate && (
                <div className="job-detail__info-row">
                  <span className="job-detail__info-label">Son Başvuru</span>
                  <span className="job-detail__info-value">{endDate}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
};

export default JobDetailPage;
